import { useState, useEffect } from "react";


export interface ImportWizardProps {
  entityLabel: string;
  onDownloadTemplate: () => Promise<void>;
  onDetectHeaders: (file: File) => Promise<any>;
  onValidate: (data: { upload_token: string; column_mapping: any }) => Promise<any>;
  onCommit: (data: { batch_id: string; row_selection: string }) => Promise<any>;
  onCheckStatus: (batchId: string) => Promise<any>;
}

type Step = "UPLOAD" | "MAPPING" | "VALIDATION" | "COMMITTING" | "PROCESSING" | "RESULT";

export function ImportWizard({
  entityLabel,
  onDownloadTemplate,
  onDetectHeaders,
  onValidate,
  onCommit,
  onCheckStatus,
}: ImportWizardProps) {
  const [step, setStep] = useState<Step>("UPLOAD");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Upload state
  const [file, setFile] = useState<File | null>(null);

  // Mapping state
  const [uploadToken, setUploadToken] = useState<string>("");
  const [detectedSheets, setDetectedSheets] = useState<Record<string, string[]>>({});
  const [requiredFields, setRequiredFields] = useState<Record<string, {field: string, label: string, required: boolean}[]>>({});
  const [columnMapping, setColumnMapping] = useState<Record<string, Record<string, string>>>({});

  // Validation state
  const [batchId, setBatchId] = useState<string>("");
  const [validationReport, setValidationReport] = useState<any>(null);
  
  // Commit state
  const [rowSelection, setRowSelection] = useState<"valid_only" | "all_or_nothing">("valid_only");
  const [finalReport, setFinalReport] = useState<any>(null);

  // Status Polling
  const [polling, setPolling] = useState(false);
  const [processingPhase, setProcessingPhase] = useState<"validate" | "commit" | null>(null);

  useEffect(() => {
    let interval: number;
    if (polling && batchId) {
      interval = window.setInterval(async () => {
        try {
          const res = await onCheckStatus(batchId);
          if (res.status === "ready_to_commit") {
            setPolling(false);
            setProcessingPhase(null);
            setValidationReport(res);
            setStep("VALIDATION");
          } else if (res.status === "completed" || res.status === "failed") {
            setPolling(false);
            setProcessingPhase(null);
            setFinalReport(res);
            setStep("RESULT");
          }
        } catch (err: any) {
          setPolling(false);
          setError(err?.response?.data?.detail || "خطا در دریافت وضعیت");
        }
      }, 2000);
    }
    return () => window.clearInterval(interval);
  }, [polling, batchId, onCheckStatus]);

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const res = await onDetectHeaders(file);
      setUploadToken(res.upload_token);
      setDetectedSheets(res.sheets || {});
      setRequiredFields(res.required_fields || {});
      
      // Auto-map matching columns
      const initialMapping: any = {};
      Object.keys(res.required_fields || {}).forEach(sheet => {
        initialMapping[sheet] = {};
        const availableCols = res.sheets?.[sheet] || [];
        res.required_fields[sheet].forEach((rf: any) => {
          if (availableCols.includes(rf.field)) {
            initialMapping[sheet][rf.field] = rf.field;
          } else if (availableCols.includes(rf.label)) {
            initialMapping[sheet][rf.field] = rf.label;
          }
        });
      });
      setColumnMapping(initialMapping);
      setStep("MAPPING");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "خطا در پردازش فایل");
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await onValidate({ upload_token: uploadToken, column_mapping: columnMapping });
      setBatchId(res.batch_id);
      if (res.status === "processing" || res.status === "validating") {
        setProcessingPhase("validate");
        setPolling(true);
        setStep("PROCESSING");
      } else {
        setValidationReport(res);
        setStep("VALIDATION");
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "خطا در اعتبارسنجی");
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await onCommit({ batch_id: batchId, row_selection: rowSelection });
      if (res.status === "committing" || res.status === "processing") {
        setProcessingPhase("commit");
        setPolling(true);
        setStep("PROCESSING");
      } else {
        setFinalReport(res);
        setStep("RESULT");
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "خطا در ثبت نهایی");
    } finally {
      setLoading(false);
    }
  };

  const renderStepIcon = (num: number, current: number, label: string) => {
    const isActive = current === num;
    const isPast = current > num;
    return (
      <div className={`step-item ${isActive ? "active" : ""} ${isPast ? "past" : ""}`}>
        <div className="step-circle">{isPast ? "✓" : num}</div>
        <div className="step-label">{label}</div>
      </div>
    );
  };

  const getStepNumber = () => {
    if (step === "UPLOAD") return 1;
    if (step === "MAPPING") return 2;
    if (step === "PROCESSING" && processingPhase === "validate") return 3;
    if (step === "VALIDATION") return 3;
    if (step === "PROCESSING" && processingPhase === "commit") return 4;
    if (step === "COMMITTING") return 4;
    if (step === "RESULT") return 5;
    return 1;
  };

  const currentStepNum = getStepNumber();

  return (
    <div className="import-wizard-container glass-panel">
      <div className="wizard-header">
        <h2>ورود گروهی اطلاعات: {entityLabel}</h2>
        <div className="wizard-steps">
          {renderStepIcon(1, currentStepNum, "آپلود فایل")}
          <div className="step-line" />
          {renderStepIcon(2, currentStepNum, "نگاشت ستون‌ها")}
          <div className="step-line" />
          {renderStepIcon(3, currentStepNum, "اعتبارسنجی")}
          <div className="step-line" />
          {renderStepIcon(4, currentStepNum, "ثبت نهایی")}
        </div>
      </div>

      {error && <div className="wizard-alert error">{error}</div>}

      <div className="wizard-body">
        {step === "UPLOAD" && (
          <div className="step-upload">
            <label className="upload-box">
              <input 
                type="file" 
                accept=".xlsx" 
                onChange={(e) => setFile(e.target.files?.[0] || null)} 
              />
              {file ? (
                <>
                  <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>📄</div>
                  <p style={{ fontWeight: 'bold', color: 'var(--color-primary)', fontSize: '1.1rem' }}>{file.name}</p>
                  <p style={{ fontSize: '0.85rem' }}>برای تغییر فایل کلیک کنید</p>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>📥</div>
                  <p style={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'var(--color-text-main)' }}>برای انتخاب فایل کلیک کنید</p>
                  <p style={{ fontSize: '0.85rem' }}>فایل Excel خود را انتخاب کنید (.xlsx)</p>
                </>
              )}
            </label>
            <div className="actions">
              <button className="btn-secondary" onClick={onDownloadTemplate}>دانلود قالب نمونه</button>
              <button className="btn-primary" onClick={handleUpload} disabled={!file || loading}>
                {loading ? "در حال پردازش..." : "مرحله بعد"}
              </button>
            </div>
          </div>
        )}

        {step === "MAPPING" && (
          <div className="step-mapping">
            <h3>نگاشت ستون‌های فایل به فیلدهای سیستم</h3>
            {Object.keys(requiredFields).map(sheet => (
              <div key={sheet} className="mapping-sheet">
                <h4>شیت: {sheet}</h4>
                <div className="mapping-grid">
                  {requiredFields[sheet]?.map(rf => (
                    <div key={rf.field} className="mapping-row">
                      <div className="mapping-label">
                        {rf.label} {rf.required && <span className="req">*</span>}
                      </div>
                      <div className="mapping-select">
                        <select 
                          value={columnMapping[sheet]?.[rf.field] || ""}
                          onChange={(e) => setColumnMapping(prev => ({
                            ...prev, 
                            [sheet]: { ...prev[sheet], [rf.field]: e.target.value }
                          }))}
                        >
                          <option value="">-- انتخاب ستون --</option>
                          {detectedSheets[sheet]?.map(col => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="actions">
              <button className="btn-secondary" onClick={() => setStep("UPLOAD")}>بازگشت</button>
              <button className="btn-primary" onClick={handleValidate} disabled={loading}>
                {loading ? "در حال اعتبارسنجی..." : "مرحله بعد"}
              </button>
            </div>
          </div>
        )}

        {step === "VALIDATION" && validationReport && (
          <div className="step-validation">
            <h3>گزارش اعتبارسنجی</h3>
            <div className="stats-cards">
              <div className="stat-card">
                <span className="stat-value">{validationReport.total_rows}</span>
                <span className="stat-label">کل ردیف‌ها</span>
              </div>
              <div className="stat-card success">
                <span className="stat-value">{validationReport.valid_rows}</span>
                <span className="stat-label">معتبر</span>
              </div>
              <div className="stat-card error">
                <span className="stat-value">{validationReport.invalid_rows}</span>
                <span className="stat-label">نامعتبر</span>
              </div>
            </div>

            {validationReport?.errors?.length > 0 && (
              <div className="error-list">
                <h4>خطاها</h4>
                <ul>
                  {validationReport.errors.map((e: any, idx: number) => (
                    <li key={idx}>
                      <strong>ردیف {e.row} ({e.sheet}):</strong> {JSON.stringify(e.errors)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="commit-options">
              <label>
                <input type="radio" value="valid_only" checked={rowSelection === "valid_only"} onChange={() => setRowSelection("valid_only")} />
                فقط ردیف‌های معتبر را ثبت کن (نادیده گرفتن خطاها)
              </label>
              <label>
                <input type="radio" value="all_or_nothing" checked={rowSelection === "all_or_nothing"} onChange={() => setRowSelection("all_or_nothing")} />
                همه یا هیچ (اگر خطایی بود هیچ چیز ثبت نشود)
              </label>
            </div>

            <div className="actions">
              <button className="btn-secondary" onClick={() => setStep("MAPPING")}>بازگشت به نگاشت</button>
              <button className="btn-primary" onClick={handleCommit} disabled={loading || (rowSelection === "all_or_nothing" && validationReport.invalid_rows > 0)}>
                {loading ? "در حال ثبت..." : "تأیید و ثبت نهایی"}
              </button>
            </div>
          </div>
        )}

        {step === "PROCESSING" && (
          <div className="step-processing">
            <div className="spinner"></div>
            <h3>
              {processingPhase === "validate"
                ? "در حال بررسی فایل..."
                : "در حال ثبت نهایی داده..."}
            </h3>
            <p>لطفاً منتظر بمانید (این صفحه را نبندید)</p>
          </div>
        )}

        {step === "RESULT" && finalReport && (
          <div className="step-result">
            <h3>{finalReport.status === "completed" ? "عملیات با موفقیت پایان یافت" : "خطا در عملیات"}</h3>
            <div className="stats-cards">
              <div className="stat-card success">
                <span className="stat-value">{finalReport.processed_rows || finalReport.committed_rows || 0}</span>
                <span className="stat-label">ردیف‌های ثبت شده</span>
              </div>
            </div>
            <div className="actions">
              <button className="btn-primary" onClick={() => {
                setStep("UPLOAD");
                setFile(null);
              }}>شروع مجدد</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
